import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaUsers, 
  FaSearch, 
  FaSync,
  FaGoogle,
  FaTimes,
  FaCheck
} from 'react-icons/fa';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';

import OrganizationalUnits from './OrganizationalUnits';
import UsersList from './UsersList';
import styles from '../CSS/Gmail.module.css';

const Gmail = ({ user, showFeedback, userRole, granularPermissions }) => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://truebavirtual.onrender.com';
  
  // Estados principales
  const [users, setUsers] = useState([]);
  const [organizationalUnits, setOrganizationalUnits] = useState([]);
  const [filteredOUs, setFilteredOUs] = useState([]);
  const [selectedOU, setSelectedOU] = useState(null);
  const [usersInOU, setUsersInOU] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backgroundRefresh, setBackgroundRefresh] = useState(false); // Nuevo: para recargas en segundo plano
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('organizational-units');
  const [isServerConnected, setIsServerConnected] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    suspendedUsers: 0,
    adminUsers: 0
  });

  // Estados para UI responsiva
  const [isMobile, setIsMobile] = useState(false);

  // Detectar dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mostrar notificación
  const showNotification = useCallback((message, type) => {
    if (showFeedback) {
      showFeedback(type, message);
    }
  }, [showFeedback]);

  // ⭐ MEJORADO: Filtrar OUs según permisos granulados (rol + grupos)
  const filterOUsByPermissions = useCallback((ous, role, granular) => {
    console.log('🔍 Filtrando OUs con permisos granulados...', granular);

    // Si hay permisos granulados, usarlos (prioridad)
    if (granular && granular.emailPermissions) {
      const { allowedOUs, canManageAll } = granular.emailPermissions;

      console.log('✅ Usando permisos granulados de email:', { allowedOUs, canManageAll });

      if (canManageAll) {
        console.log('👑 Acceso total a todas las OUs (granular)');
        return ous;
      }

      if (!Array.isArray(allowedOUs) || allowedOUs.length === 0) {
        console.log('⚠️ Sin OUs permitidas en permisos granulados');
        return [];
      }

      console.log('🔒 Filtrando OUs según permisos granulados:', allowedOUs);

      const filterTree = (ouList) => {
        return ouList.filter(ou => {
          const isAllowed = allowedOUs.includes(ou.orgUnitPath);

          if (ou.children && ou.children.length > 0) {
            ou.children = filterTree(ou.children);
            return isAllowed || ou.children.length > 0;
          }

          return isAllowed;
        });
      };

      const filtered = filterTree(JSON.parse(JSON.stringify(ous)));
      console.log('✅ OUs filtradas (granular):', filtered.length);
      return filtered;
    }

    // Fallback al sistema antiguo de rol
    console.log('⚠️ Usando permisos del rol (fallback)');

    if (!role) {
      console.log('🔓 Sin rol definido - mostrando todas las OUs');
      return ous;
    }

    if (role.id === 'admin') {
      console.log('👑 Usuario admin - mostrando todas las OUs');
      return ous;
    }

    const hasEmailPermission = role.permissions?.includes('correos');
    if (!hasEmailPermission) {
      console.log('⚠️ Sin permiso de correos - sin acceso a OUs');
      return [];
    }

    const emailPerms = role.emailPermissions;
    if (!emailPerms) {
      console.log('⚠️ Sin configuración de emailPermissions - sin acceso');
      return [];
    }

    if (emailPerms.canManageAll) {
      console.log('✅ Acceso a todas las OUs');
      return ous;
    }

    const allowedOUs = emailPerms.allowedOUs || [];
    if (allowedOUs.length === 0) {
      console.log('⚠️ Lista de OUs permitidas vacía');
      return [];
    }

    console.log('🔍 Filtrando OUs - Permitidas:', allowedOUs);

    const filterTree = (ouList) => {
      return ouList.filter(ou => {
        const isAllowed = allowedOUs.includes(ou.orgUnitPath);

        if (ou.children && ou.children.length > 0) {
          ou.children = filterTree(ou.children);
          return isAllowed || ou.children.length > 0;
        }

        return isAllowed;
      });
    };

    const filtered = filterTree(JSON.parse(JSON.stringify(ous)));
    console.log('✅ OUs filtradas:', filtered.length);
    return filtered;
  }, []);

  // ⭐ MEJORADO: Aplicar filtro cuando cambian las OUs, rol o permisos granulados
  useEffect(() => {
    if (organizationalUnits.length > 0) {
      const filtered = filterOUsByPermissions(organizationalUnits, userRole, granularPermissions);
      setFilteredOUs(filtered);

      if (filtered.length === 0 && userRole && userRole.id !== 'admin') {
        showNotification('No tienes acceso a ninguna unidad organizativa', 'warning');
      }
    }
  }, [organizationalUnits, userRole, granularPermissions, filterOUsByPermissions, showNotification]);

  // Verificar estado del servidor y cargar datos
  const checkServerAndLoadData = useCallback(async () => {
    try {
      console.log('🔍 Conectando al servidor Gmail...');
      setLoading(true);
      
      const healthResponse = await fetch(`${SERVER_URL}/api/workspace/health`);
      if (!healthResponse.ok) throw new Error('Servidor no disponible');
      
      const health = await healthResponse.json();
      setIsServerConnected(health.authenticated);
      
      if (!health.authenticated) {
        showNotification("El servidor no está autenticado con Google Workspace", "error");
        setIsInitialLoad(false);
        return;
      }

      console.log('📥 Cargando datos desde servidor...');
      const dataResponse = await fetch(`${SERVER_URL}/api/workspace/data`);
      if (!dataResponse.ok) throw new Error('Error obteniendo datos');
      
      const data = await dataResponse.json();

      const units = data.organizationUnits || [];
      const organizedUnits = organizeOUsHierarchy(units);
      setOrganizationalUnits(organizedUnits);

      const loadedUsers = data.users || [];
      setUsers(loadedUsers);
      
      if (data.stats) {
        setStats(data.stats);
      } else {
        const totalUsers = loadedUsers.length;
        const activeUsers = loadedUsers.filter(u => !u.suspended).length;
        const suspendedUsers = loadedUsers.filter(u => u.suspended).length;
        const adminUsers = loadedUsers.filter(u => u.isAdmin).length;
        
        setStats({ 
          totalUsers, 
          activeUsers, 
          suspendedUsers, 
          adminUsers 
        });
      }
      
      if (selectedOU) {
        const filteredUsers = loadedUsers.filter(user => 
          user.orgUnitPath === selectedOU
        );
        setUsersInOU(filteredUsers);
      }
      
      setLastUpdate(data.cacheTime);
      setInitialLoadDone(true);
      setIsInitialLoad(false);
      
      console.log('✅ Datos de Gmail cargados correctamente');
      if (!isInitialLoad) {
        showNotification("Datos actualizados correctamente", "success");
      }

    } catch (error) {
      console.error('❌ Error:', error);
      setIsServerConnected(false);
      setIsInitialLoad(false);
      showNotification("Error conectando con el servidor: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [SERVER_URL, showNotification, selectedOU, isInitialLoad]);

  // Actualizar datos (forzar refresh en el servidor)
  const refreshGmailData = useCallback(async () => {
    if (!isServerConnected) return;

    try {
      setLoading(true);
      console.log('🔄 Actualizando datos de Gmail...');

      const response = await fetch(`${SERVER_URL}/api/workspace/refresh`, { method: 'POST' });
      if (!response.ok) throw new Error('Error actualizando datos');

      const result = await response.json();
      
      await checkServerAndLoadData();
      
      showNotification("Datos actualizados correctamente", "success");
      
    } catch (error) {
      console.error('❌ Error actualizando:', error);
      showNotification("Error al actualizar datos: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [SERVER_URL, isServerConnected, checkServerAndLoadData, showNotification]);

  // Obtener usuarios de una unidad organizativa específica
  const fetchUsersInOU = useCallback(async (orgUnitPath) => {
    setLoading(true);
    
    try {
      const filteredUsers = users.filter(user => 
        user.orgUnitPath === orgUnitPath
      );
      
      setUsersInOU(filteredUsers);
      setSelectedOU(orgUnitPath);
      
    } catch (error) {
      console.error('Error filtering users in OU:', error);
      showNotification('Error al cargar usuarios de la unidad organizativa', 'error');
    } finally {
      setLoading(false);
    }
  }, [users, showNotification]);

  // Recargar datos después de operaciones en UsersList (silencioso, sin loading global)
  const handleUserDataChange = useCallback(async () => {
    console.log('🔄 Recargando datos después de operación (modo silencioso)...');

    try {
      setBackgroundRefresh(true);

      const dataResponse = await fetch(`${SERVER_URL}/api/workspace/data`);
      if (!dataResponse.ok) throw new Error('Error obteniendo datos');

      const data = await dataResponse.json();

      const units = data.organizationUnits || [];
      const organizedUnits = organizeOUsHierarchy(units);
      setOrganizationalUnits(organizedUnits);

      const loadedUsers = data.users || [];
      setUsers(loadedUsers);

      if (data.stats) {
        setStats(data.stats);
      } else {
        const totalUsers = loadedUsers.length;
        const activeUsers = loadedUsers.filter(u => !u.suspended).length;
        const suspendedUsers = loadedUsers.filter(u => u.suspended).length;
        const adminUsers = loadedUsers.filter(u => u.isAdmin).length;

        setStats({
          totalUsers,
          activeUsers,
          suspendedUsers,
          adminUsers
        });
      }

      // Actualizar usuarios de la OU seleccionada sin perder la selección
      if (selectedOU) {
        const filteredUsers = loadedUsers.filter(user =>
          user.orgUnitPath === selectedOU
        );
        setUsersInOU(filteredUsers);
      }

      setLastUpdate(data.cacheTime);
      console.log('✅ Datos recargados silenciosamente');

    } catch (error) {
      console.error('❌ Error recargando datos:', error);
      showNotification("Error al actualizar datos: " + error.message, "error");
    } finally {
      setBackgroundRefresh(false);
    }
  }, [SERVER_URL, selectedOU, showNotification]);

  // Funciones auxiliares
  const organizeOUsHierarchy = (ous) => {
    const ouMap = new Map();
    const rootOUs = [];
    
    ous.forEach(ou => {
      ouMap.set(ou.orgUnitPath, {
        ...ou,
        children: []
      });
    });
    
    ous.forEach(ou => {
      const ouData = ouMap.get(ou.orgUnitPath);
      const pathParts = ou.orgUnitPath.split('/').filter(part => part !== '');
      
      if (pathParts.length === 1) {
        rootOUs.push(ouData);
      } else {
        const parentPath = '/' + pathParts.slice(0, -1).join('/');
        const parent = ouMap.get(parentPath);
        if (parent) {
          parent.children.push(ouData);
        }
      }
    });
    
    return rootOUs;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Effects
  useEffect(() => {
    checkServerAndLoadData();
  }, []);

  // Filtrar usuarios según término de búsqueda
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return user.primaryEmail?.toLowerCase().includes(search) ||
           user.name?.givenName?.toLowerCase().includes(search) ||
           user.name?.familyName?.toLowerCase().includes(search);
  });

  const filteredUsersInOU = usersInOU.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return user.primaryEmail?.toLowerCase().includes(search) ||
           user.name?.givenName?.toLowerCase().includes(search) ||
           user.name?.familyName?.toLowerCase().includes(search);
  });

  // Solo mostrar el spinner en la carga inicial
  if (isInitialLoad && loading) {
    return (
      <div className={styles.initialLoadingContainer}>
        <Loader2 size={40} className={styles.loadingSpinner} />
        <p>Cargando Gmail Workspace...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header optimizado y minimalista */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          {/* Status indicator */}
          <div className={styles.statusIndicator}>
            <FaGoogle className={styles.googleIcon} />
            <span className={`${styles.statusDot} ${isServerConnected ? styles.connected : styles.disconnected}`} />
            {backgroundRefresh && (
              <span className={styles.backgroundRefresh} title="Actualizando datos...">
                <FaSync className={styles.spinning} />
              </span>
            )}
            {!isMobile && lastUpdate && !backgroundRefresh && (
              <span className={styles.lastUpdate}>
                {formatDate(lastUpdate)}
              </span>
            )}
          </div>

          {/* Search bar */}
          <div className={styles.searchBar}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className={styles.clearButton}
                aria-label="Limpiar búsqueda"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Refresh button */}
          <button
            onClick={refreshGmailData}
            className={styles.refreshButton}
            disabled={loading || !isServerConnected}
            aria-label="Actualizar datos"
            title="Actualizar datos"
          >
            <FaSync className={loading ? styles.spinning : ''} />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.content}>
        {/* Server connection error */}
        {!isServerConnected && !isInitialLoad && (
          <div className={styles.serverError}>
            <div className={styles.serverErrorContent}>
              <AlertCircle size={48} className={styles.serverErrorIcon} />
              <h4>Servidor no disponible</h4>
              <p>No se puede conectar con el servidor de Google Workspace</p>
              
              <button
                className={styles.retryButton}
                onClick={checkServerAndLoadData}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? styles.spinning : ''} />
                Reintentar conexión
              </button>
            </div>
          </div>
        )}

        {/* Main content when connected */}
        {isServerConnected && (
          <div className={styles.ouSection}>
            <div className={`${styles.ouContainer} ${isMobile ? styles.mobileOuContainer : ''}`}>
              <OrganizationalUnits
                organizationalUnits={filteredOUs} 
                selectedOU={selectedOU}
                onOUSelect={fetchUsersInOU}
                isMobile={isMobile}
              />
              
              <UsersList
                users={filteredUsersInOU}
                selectedOU={selectedOU}
                isMobile={isMobile}
                serverUrl={SERVER_URL}
                showNotification={showNotification}
                onDataChange={handleUserDataChange}
                isOUView={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gmail;